import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import AddIcon from '@mui/icons-material/Add';
import BoltIcon from '@mui/icons-material/Bolt';
import ScienceIcon from '@mui/icons-material/Science';
import useVoyagerStore from '../../stores/useVoyagerStore';
import DomainChip from './DomainChip';
import PerspectiveChip from './PerspectiveChip';
import { getDraft, setDraft, subscribeDrafts } from './composerDrafts';
import { Button, Typography } from '@mui/material';
import { composerRoute, sendToAgent, ComposerRoute } from './composerRouting';
import Chip from '@mui/material/Chip';
import { ATTACH_ACCEPT, uploadDocument, type Attachment } from '../../apis';
import { followUpOf } from '../../stores/slices/landingAgentSlice';
import { CORRECTION_PREFILL, useComposerReply } from './simple/composerReply';

export const ATTACHMENT_REFUSED = 'A file can be sent to Voyager, not to an agent. Remove the @address to send it.';
/** #1508: a reply from Correct this is asked about its answer, so it goes to Stroma — never to an agent, which would drop it. */
export const REPLY_REFUSED = 'A correction goes to Stroma, not to an agent. Remove the @address, or stop replying to send it to the agent.';

interface Props {
  variant?: 'hero' | 'docked';
  /**
   * Keeps the typed-but-unsent text per tab (#182): switching tabs and coming
   * back finds the draft where it was. No key = the composer forgets on
   * unmount, as before.
   */
  draftKey?: string;
  onSubmit?: (text: string) => void | boolean;
  disabledReason?: string;
  initialAddress?: string;
  /** Whether this composer may attach a file (#763): only where the answer is a landing answer the asker will see. */
  attachments?: boolean;
  /** What an empty composer says, where the screen has its own words. */
  placeholder?: string;
}

export default function ChatComposer({ variant = 'hero', draftKey, onSubmit, disabledReason, initialAddress = '', attachments = true, placeholder }: Props) {
  const generation = useVoyagerStore((s) => s.agentsGeneration);
  const domainsOn = useVoyagerStore((s) => s.features.domains);
  const ensureFeatures = useVoyagerStore((s) => s.ensureFeatures);
  // Cached once it loads; a failed read is retried on the next mount (#464).
  useEffect(() => { ensureFeatures(); }, [ensureFeatures]);
  const agents = useVoyagerStore((s) => s.agents);
  const fetchAgents = useVoyagerStore((s) => s.fetchAgents);
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState<Extract<ComposerRoute, { kind: 'agent' }> | null>(null);
  useEffect(() => { void fetchAgents({ ifNeeded: true }); }, [fetchAgents, generation]);
  useEffect(() => { setPreview(null); setNote(''); }, [generation]);
  // "Select a perspective first" must not still be sitting beside the
  // perspective you just selected — the reason is gone, so the note is too
  const perspectiveId = useVoyagerStore((s) => s.selectedPerspectiveId);
  useEffect(() => { if (perspectiveId) setNote(''); }, [perspectiveId]);
  const [text, setTextState] = useState(() => ((draftKey ? getDraft(draftKey) : '') || initialAddress));
  const setText = (value: string) => {
    setPreview(null); setNote('');
    setTextState(value);
    if (draftKey) setDraft(draftKey, value);
  };
  // A workspace switch clears every draft; what is on screen clears with it. A fill reaches only its own key (#1504).
  useEffect(() => subscribeDrafts((key) => {
    if (key !== undefined && key !== draftKey) return;
    if (key !== undefined) { setPreview(null); setNote(''); }
    setTextState((draftKey ? getDraft(draftKey) : '') || initialAddress);
    if (key !== undefined) inputRef.current?.focus();
  }), [draftKey, initialAddress]);
  const status = useVoyagerStore((s) => s.landingAgentStatus);
  const sendLandingAgentQuery = useVoyagerStore((s) => s.sendLandingAgentQuery);
  const cancelLandingAgent = useVoyagerStore((s) => s.cancelLandingAgent);
  const mode = useVoyagerStore((s) => s.landingAgentMode);
  const setMode = useVoyagerStore((s) => s.setLandingAgentMode);
  // the open thread's earlier answers, filled from its own read (#1270): held while loading, a notice if they failed
  const earlier = useVoyagerStore((s) => (s.currentThreadId && !onSubmit ? s.threadAnswers[s.currentThreadId] : undefined));
  const disabled = !!disabledReason || earlier === 'loading' || status === 'running' || status === 'clarifying';
  const isRunning = status === 'running';
  const inputRef = React.useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    if (!disabled && (draftKey === 'home' || draftKey === 'ask') && getDraft(draftKey || 'home')) inputRef.current?.focus();
  }, [disabled, draftKey]);
  // A file attached here (#763) is kept for its uploader, then read by the Document agent with the question
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef<HTMLInputElement | null>(null);
  const attach = (file: File | undefined) => {
    if (!file) return;
    setUploading(true); setNote('');
    uploadDocument(file).then((kept) => setAttachment(kept),
      () => setNote('That file could not be attached: a .pptx, .pdf, .xlsx or .csv of at most 20 MB can be.'))
      .finally(() => { setUploading(false); if (fileRef.current) fileRef.current.value = ''; });
  };
  // #1508: "Correct this" on a data answer makes this composer a reply to THAT answer — prefilled, focused, and
  // sent about that answer (`followUpOf`), as every follow-up about a displayed answer is. A composer with its own
  // submit (an objective's) never takes one.
  const reply = useComposerReply((s) => (draftKey && !onSubmit ? s.replies[draftKey] || null : null));
  const clearOwnReply = useComposerReply((s) => s.clearReply);
  const clearReply = (key?: string) => { if (key) clearOwnReply(key); };
  // bound only while that answer is on screen: an answer no longer here is replied to by no one, and says nothing
  const replyTurn = useVoyagerStore((s) => (reply ? s.landingAgentTurns.find((t) => t.id === reply.turnId) || null : null));
  useEffect(() => {
    if (!reply) return;
    if (!(draftKey ? getDraft(draftKey) : text).trim()) setText(CORRECTION_PREFILL);
    const t = setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange?.(el.value.length, el.value.length);
    }, 0);
    return () => clearTimeout(t);
    // only a new "Correct this" click prefills and focuses
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reply?.nonce]);
  const binding = reply && replyTurn ? followUpOf(replyTurn) : undefined;
  // the prefill alone says nothing yet
  const saysNothing = !!binding && text.trim() === CORRECTION_PREFILL.trim();
  const canSend = !disabled && !uploading && !saysNothing && (text.trim().length > 0 || !!attachment);

  const send = () => {
    if (!canSend) return;
    if (attachment) {
      // a file goes to the Document agent, never to an agent or a group: refused there, and said
      const addressed = composerRoute(text, agents);
      if (addressed.kind !== 'voyager') { setNote(ATTACHMENT_REFUSED); return; }
      if (sendLandingAgentQuery(text.trim() || `Read ${attachment.name}`, binding ? { ...binding, attachment } : { attachment }) === false) {
        setNote(useVoyagerStore.getState().landingAgentError || 'That question could not be sent.');
        return;
      }
      setAttachment(null); setNote(''); setText(initialAddress); clearReply(draftKey);
      return;
    }
    const route = composerRoute(text, agents);
    if (route.kind === 'note') { setNote(route.text); return; }
    if (route.kind === 'agent') {
      if (binding) { setNote(REPLY_REFUSED); return; }
      if (route.direction) { setPreview(route); return; }
      sendToAgent(route); setText(initialAddress); clearReply(draftKey); return;
    }
    // keep the draft when the question did not start, and SAY why: clearing the
    // box on a refusal loses what was typed and shows nothing for it, so the
    // question just vanishes — which reads as broken rather than as a prompt
    if ((onSubmit ? onSubmit(text) : binding ? sendLandingAgentQuery(text, binding) : sendLandingAgentQuery(text)) === false) {
      setNote(useVoyagerStore.getState().landingAgentError || 'That question could not be sent.');
      return;
    }
    setNote('');
    setText(initialAddress);
    clearReply(draftKey);
  };

  return (
    <Box
      data-testid="chat-composer"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        px: 2,
        pt: 1.5,
        pb: 1,
        border: 1,
        borderColor: 'divider',
        borderRadius: '22px',
        bgcolor: 'background.paper',
        boxShadow: 'var(--shadow)',
        textAlign: 'left',
        p: { xs: 1.5, sm: 2 },
        background: 'linear-gradient(180deg, var(--surface), var(--surface2))',
        '&:focus-within': { borderColor: 'var(--accent)' },
      }}
    >
      {preview && <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption">Direction to {preview.agent.name} · applies next run</Typography>
        <Button size="small" disabled={disabled} onClick={() => {
          const current = composerRoute(text, useVoyagerStore.getState().agents);
          if (current.kind !== 'agent' || current.agent.id !== preview.agent.id) { setPreview(null); setNote('The agent changed. Check the address and send again.'); return; }
          if (binding) { setPreview(null); setNote(REPLY_REFUSED); return; }
          sendToAgent(current); setText(initialAddress);
        }}>Send</Button>
        <Button size="small" onClick={() => setPreview(null)}>Cancel</Button>
      </Box>}
      {note && <Typography role="status" variant="caption">{note}</Typography>}
      {binding && replyTurn && <Chip size="small" variant="outlined" data-testid="composer-reply"
        label={`Replying to: ${replyTurn.userText}`} onDelete={() => clearReply(draftKey)}
        sx={{ alignSelf: 'flex-start', maxWidth: '100%', borderRadius: 99, color: 'var(--ink-2)' }} />}
      {earlier === 'failed' && <Typography role="status" variant="caption">
        Earlier answers couldn't be loaded; this question is asked without them.</Typography>}
      <TextField
        inputRef={inputRef}
        autoFocus={(draftKey === 'home' || draftKey === 'ask') && !!getDraft(draftKey || 'home')}
        multiline
        minRows={variant === 'hero' ? 2 : 1}
        maxRows={8}
        fullWidth
        placeholder={disabledReason || (earlier === 'loading' ? 'Loading earlier answers…' : placeholder || "Ask about your data…")}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            send();
          }
        }}
        variant="standard"
        sx={{ '& textarea::placeholder': { color: 'text.secondary', opacity: 1 } }}
        inputProps={{ 'aria-label': 'Ask about your data' }}
        InputProps={{ disableUnderline: true }}
        disabled={disabled}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        <input ref={fileRef} type="file" accept={ATTACH_ACCEPT} hidden data-testid="composer-file"
          onChange={(e) => attach(e.target.files?.[0])} />
        <Tooltip title={!attachments || onSubmit ? 'Attachments are not available here' : 'Attach a deck, PDF or spreadsheet'}>
          <span>
            <IconButton size="small" disabled={!attachments || !!onSubmit || disabled || uploading} aria-label="Attach a file"
              onClick={() => fileRef.current?.click()}>
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {(attachment || uploading) && <Chip size="small" label={uploading ? 'Attaching…' : attachment!.name}
          onDelete={attachment ? () => setAttachment(null) : undefined} />}

        {domainsOn && <DomainChip />}
        <PerspectiveChip />

        <Box sx={{ flex: 1 }} />

        <Box role="group" aria-label="Research mode" sx={{ display: 'flex', gap: 0.25, p: 0.375, borderRadius: 2, bgcolor: 'var(--sunk)', border: '1px solid var(--line)' }}>
          {(['fast', 'deep'] as const).map((value) => <Tooltip key={value} title={value === 'fast' ? 'Fast — Sonnet planner' : 'Deep Research — Opus planner'}>
            <Button size="small" aria-pressed={mode === value} onClick={() => setMode(value)} startIcon={value === 'fast' ? <BoltIcon /> : <ScienceIcon />} sx={{ minWidth: 0, px: 1, color: mode === value ? 'var(--accent-ink)' : 'text.secondary', bgcolor: mode === value ? 'background.paper' : 'transparent', boxShadow: mode === value ? '0 1px 2px var(--shadow-c)' : 'none' }}>
              {value === 'fast' ? 'Fast' : 'Deep Research'}
            </Button>
          </Tooltip>)}
        </Box>

        {/* F4 (#1858 §5): while the answer runs, its one Stop is here — a round button, for a durable run too */}
        {isRunning ? (
          <Tooltip title="Stop">
            <IconButton aria-label="Stop" data-testid="composer-stop" onClick={cancelLandingAgent}
              sx={{ ml: 0.5, width: 34, height: 34, bgcolor: 'var(--ink)', color: 'var(--surface, #fff)',
                '&:hover': { bgcolor: 'var(--ink-2)' } }}>
              <StopIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Button aria-label="Send question" variant="contained" color="primary" disabled={!canSend} onClick={send} startIcon={<SendIcon />} sx={{ ml: 0.5 }}>Send</Button>
        )}
      </Box>
    </Box>
  );
}
